import { FC, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Collapse,
    Divider,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { SCUILoadingLinear } from '../../../../atomic/atoms';
import type { StoreOrderDetailView } from './customerStoreOrders.types';
import {
    formatDiscountAmountLabel,
    formatSizeColorLabel,
    formatStoreOrderPrice,
    isStoreReceiptDownloadEnabled,
    resolveLineItemPriceDisplay,
} from './customerStoreOrders.helpers';
import { CSOP } from './customerStoreOrdersPaper.classes';

interface CustomerStoreOrderExpandedPanelProps {
    detail: StoreOrderDetailView | null;
    loading: boolean;
    onDownloadReceipt: () => void;
    receiptDownloading: boolean;
}

export const CustomerStoreOrderExpandedPanel: FC<CustomerStoreOrderExpandedPanelProps> = ({
    detail,
    loading,
    onDownloadReceipt,
    receiptDownloading,
}) => {
    const { t } = useTranslation();
    const [discountsOpen, setDiscountsOpen] = useState(false);

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <SCUILoadingLinear display={true} />
            </Box>
        );
    }

    if (!detail) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">{t('customerStoreOrders.detailLoadFailed')}</Typography>
            </Box>
        );
    }

    const hasDiscountLines = (detail.summary.discountLines?.length ?? 0) > 0;
    const grandTotalDisplay =
        detail.summary.grandTotalStr ??
        (detail.summary.grandTotal != null ? formatStoreOrderPrice(detail.summary.grandTotal) : '-');
    const subTotalDisplay =
        detail.summary.subTotalStr ??
        (detail.summary.subTotal != null ? formatStoreOrderPrice(detail.summary.subTotal) : '-');
    const discountDisplay = formatDiscountAmountLabel(
        detail.summary.discountTotalStr ??
            (detail.summary.discountTotal != null ? formatStoreOrderPrice(detail.summary.discountTotal) : '-')
    );
    const paymentAmount =
        detail.payment.amountStr ??
        (detail.payment.amount != null ? formatStoreOrderPrice(detail.payment.amount) : grandTotalDisplay);

    const renderLineItem = (item: StoreOrderDetailView['items'][number]) => {
        const prices = resolveLineItemPriceDisplay(item);
        return (
            <Box
                key={item.id}
                className={CSOP.productRow}
                sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 2,
                }}
            >
                <Avatar
                    src={item.imageUrl || undefined}
                    variant="rounded"
                    sx={{ width: 72, height: 72, bgcolor: 'grey.200' }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {item.brand && (
                        <Typography variant="caption" color="text.secondary">
                            {item.brand}
                        </Typography>
                    )}
                    <Typography variant="body2" fontWeight={600}>
                        {item.productName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {formatSizeColorLabel(item.size, item.color)}
                        {'  '}
                        {t('customerStoreOrders.quantity')}: {item.quantity}
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                        {prices.showDiscount && prices.list && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textDecoration: 'line-through' }}
                            >
                                {prices.list}
                            </Typography>
                        )}
                        <Typography
                            variant="body2"
                            fontWeight={700}
                            color={prices.showDiscount ? 'error.main' : 'text.primary'}
                        >
                            {prices.sale}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Box className={CSOP.expandedPanel} sx={{ p: 2 }}>
            <Stack spacing={2}>
                {(detail.storeName || detail.storeAddress) && (
                    <Box>
                        {detail.storeName && (
                            <Typography variant="subtitle2" fontWeight={700}>
                                {detail.storeName}
                            </Typography>
                        )}
                        {detail.storeAddress && (
                            <Typography variant="body2" color="text.secondary">
                                {detail.storeAddress}
                            </Typography>
                        )}
                    </Box>
                )}
                {isStoreReceiptDownloadEnabled(detail.isReceiptDownloadActive) && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            className={CSOP.inputLinkViewReceipt}
                            variant="outlined"
                            size="small"
                            startIcon={<ReceiptLongIcon />}
                            disabled={receiptDownloading}
                            onClick={onDownloadReceipt}
                        >
                            {t('customerStoreOrders.downloadReceipt')}
                        </Button>
                    </Box>
                )}

                {detail.items.map((item) => renderLineItem(item))}

                {detail.returnItems?.length ? (
                    <Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            {t('customerStoreOrders.returnItems')}
                        </Typography>
                        {detail.returnItems.map((item) => renderLineItem(item))}
                    </Box>
                ) : null}

                <Box className={CSOP.paymentSection}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                        {t('customerStoreOrders.paymentInformation')}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <CreditCardIcon color="action" fontSize="small" sx={{ mt: 0.25 }} />
                            <Box>
                                <Typography variant="body2">{detail.payment.methodLabel}</Typography>
                                {detail.payment.bankLabel && (
                                    <Typography variant="caption" color="text.secondary">
                                        {detail.payment.bankLabel}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Typography variant="body2" fontWeight={600}>
                            {paymentAmount}
                        </Typography>
                    </Box>
                </Box>

                <Divider />

                <Box className={CSOP.summarySection}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography variant="body2">{t('customerStoreOrders.total')}</Typography>
                        <Typography variant="body2">{subTotalDisplay}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="error.main">
                                {t('customerStoreOrders.discounts')}
                            </Typography>
                            {hasDiscountLines && (
                                <IconButton
                                    size="small"
                                    onClick={() => setDiscountsOpen((prev) => !prev)}
                                    aria-label="toggle discounts"
                                >
                                    {discountsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" color="error.main" fontWeight={600}>
                            {discountDisplay}
                        </Typography>
                    </Box>
                    {hasDiscountLines && (
                        <Collapse in={discountsOpen}>
                            <Stack spacing={0.5} sx={{ pl: 2, pb: 1 }}>
                                {detail.summary.discountLines?.map((line) => (
                                    <Box key={line.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {line.label}
                                        </Typography>
                                        <Typography variant="caption" color="error.main">
                                            {formatDiscountAmountLabel(
                                                line.amountStr ?? formatStoreOrderPrice(line.amount)
                                            )}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Collapse>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, mt: 0.5 }}>
                        <Typography variant="body1" fontWeight={700}>
                            {t('customerStoreOrders.grandTotal')}
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                            {grandTotalDisplay}
                        </Typography>
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
};
